import React, { useEffect, useMemo, useReducer } from 'react';

import { AccountType, CloudProviders } from '@ariksa/cloud-account';
import { CloudAccountGetResponse } from '@ariksa/cloud-account/api';
import { Box, Flex, HStack, Radio, RadioGroup, Stack } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { customTheme } from 'theme';

import {
  Card,
  DottedUnderlineLink,
  FormFieldBox,
  PageHeaderWithIcon,
  StopSignLabel,
  WarningAlert,
} from 'components/DataDisplay';
import { Form, FormConfirmationField, Select } from 'components/DataEntry';
import { OktaIcon, SnowflakeIcon } from 'components/Icons';
import { selectApp } from 'containers/App/selectors';
import { renderRadioButton } from 'containers/Setup/CloudAccounts/CloudAccountWizard/Components/utils/utils';
import { radioBoxStyles } from 'containers/Setup/CloudAccounts/CloudAccountWizard/styles';
import { selectCloudAccounts } from 'containers/Setup/CloudAccounts/selectors';
import { actions } from 'containers/Setup/CloudAccounts/slice';

export const EnableDataAccessManagementForSnowflake = () => {
  const dispatch = useDispatch();
  const {
    accountDetails,
    dataAccessManagement,
    identityProviderAccounts,
  } = useSelector(selectCloudAccounts);
  const { cloudAccounts } = useSelector(selectApp);
  const params = useParams<{ id: string }>();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const jitSetupPath = pathname.startsWith('/setup/jit')
    ? '/setup/jit'
    : '/jit/setup';

  const [state, updateState] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      permissionType: 'existing',
      isConfirm: false,
      roleArn: '',
      externalId: '',
      regions: 'active',
      roleName: '',
      identityProvider: 'okta',
      oktaAccount: {},
      scimIntegrationIdPAndAriksa: false,
      scimIntegrationIdPAndSnowflake: false,
      snowflakeAccount: {},
    },
  );

  const accountOptions = useMemo(() => {
    const accounts = [...cloudAccounts.data, accountDetails.data].filter(
      (account): account is CloudAccountGetResponse =>
        !!account?.uuid &&
        account.cloud_type === CloudProviders.Snowflake &&
        account.data_access_mgmt_status !== 'SUCCESS',
    );

    return accounts
      .filter(
        (account, index, allAccounts) =>
          allAccounts.findIndex(item => item.uuid === account.uuid) === index,
      )
      .map(account => ({
        label: `${account.name} (${account.cloud_account_id})`,
        value: account.uuid,
        data: account,
        icon: <SnowflakeIcon />,
      }));
  }, [accountDetails.data, cloudAccounts.data]);

  useEffect(() => {
    const selectedAccount =
      accountOptions.find(account => account.value === params.id) ??
      accountOptions[0];

    if (selectedAccount?.value !== state.snowflakeAccount?.value) {
      updateState({ snowflakeAccount: selectedAccount ?? {} });
    }
  }, [accountOptions, params.id, state.snowflakeAccount?.value]);

  const oktaAccountOptions = useMemo(
    () =>
      identityProviderAccounts.data.map(account => ({
        label: `${account.name} (${account.cloud_account_id})`,
        value: account.uuid,
        icon: <OktaIcon />,
      })),
    [identityProviderAccounts.data],
  );

  useEffect(() => {
    if (state.identityProvider !== 'okta') return;

    dispatch(
      actions.getIdentityProviderAccounts({
        q: {
          accountType: AccountType.IdentityProvider,
          cloudType: CloudProviders.Okta,
          page: 1,
          size: 1000,
        },
      }),
    );
  }, [dispatch, state.identityProvider]);

  useEffect(() => {
    if (state.oktaAccount?.value || !oktaAccountOptions.length) return;
    updateState({ oktaAccount: oktaAccountOptions[0] });
  }, [oktaAccountOptions, state.oktaAccount?.value]);

  const onSubmit = () => {
    const accountId = state.snowflakeAccount?.value;
    if (!accountId) return;

    const isCustomRole = state.permissionType === 'create';

    dispatch(
      actions.enableDataAccessManagement({
        q: {
          dataAccessMgmtRequest: {
            account_id: accountId,
            identity_provider_type:
              state.identityProvider === 'okta'
                ? state.identityProvider
                : undefined,
            identity_provider_id:
              state.identityProvider === 'okta'
                ? state.oktaAccount?.value
                : undefined,
            snowflake_role: isCustomRole ? state.roleName.trim() : undefined,
          },
        },
        onSuccess: () => navigate(jitSetupPath),
      }),
    );
  };

  const selectedAccount = state.snowflakeAccount?.data;

  return (
    <Card styles={{ cardContent: { pl: 32 } }}>
      <Form
        title={
          <PageHeaderWithIcon
            label="Enable Data Access Management"
            icon={<SnowflakeIcon />}
            iconFilled={false}
          />
        }
        schema={{
          account: {
            type: 'react-select',
            label: 'Snowflake account',
            options: accountOptions,
            value: state.snowflakeAccount,
            onChange: snowflakeAccount => updateState({ snowflakeAccount }),
            isLoading: cloudAccounts.loading || accountDetails.isLoading,
            isDisabled: !accountOptions.length,
            showIconInValueContainer: true,
          },
          name: {
            type: 'custom-with-form-control',
            label: 'Connector name',
            component: (
              <FormFieldBox>
                {selectedAccount?.name ?? accountDetails.data?.name} (
                {selectedAccount?.cloud_account_id ??
                  accountDetails.data?.cloud_account_id}
                )
              </FormFieldBox>
            ),
          },
          identityProvider: {
            type: 'custom',
            component: () => (
              <Box {...radioBoxStyles}>
                <Stack spacing={4}>
                  <Stack spacing={0}>
                    <Box fontSize="md">
                      Which directory identity provider do you use
                    </Box>
                    <Box fontSize="sm" color={customTheme.colors.gray[250]}>
                      Please set up SCIM integration between your directory
                      identity provider and Snowflake
                    </Box>
                  </Stack>
                  <RadioGroup
                    value={state.identityProvider}
                    onChange={value => updateState({ identityProvider: value })}
                  >
                    <HStack spacing={10}>
                      <Radio value="okta" size="lg">
                        <Box fontSize="md">Okta</Box>
                      </Radio>
                      <Radio value="none" size="lg">
                        <Box fontSize="md">None</Box>
                      </Radio>
                    </HStack>
                  </RadioGroup>
                  {state.identityProvider === 'okta' && (
                    <Stack spacing={1}>
                      <Box fontSize="md">Okta Account</Box>
                      <Select
                        placeholder="Select Okta account"
                        options={oktaAccountOptions}
                        value={state.oktaAccount}
                        onChange={oktaAccount => updateState({ oktaAccount })}
                        isLoading={identityProviderAccounts.isLoading}
                        showClearIndicator={false}
                        isAFilter
                        showIconInValueContainer
                      />
                    </Stack>
                  )}
                </Stack>
              </Box>
            ),
          },
          ...(state.identityProvider === 'okta'
            ? {
                scimIntegration: {
                  type: 'custom',
                  component: () => (
                    <Stack>
                      <StopSignLabel label="To proceed, configure both SCIM integrations indicated below" />
                      <Flex justify="flex-end">
                        <DottedUnderlineLink
                          onClick={() =>
                            navigate('/setup/integrations/add/okta')
                          }
                          label="Go to Setup"
                        />
                      </Flex>
                      <FormConfirmationField
                        header="Configure SCIM integration between Identity Provider and Ariksa"
                        subHeader="Ariksa uses this to manage data access control for users and groups in Identity Provider"
                        isChecked={state.scimIntegrationIdPAndAriksa}
                        onClick={() =>
                          updateState({
                            scimIntegrationIdPAndAriksa: !state.scimIntegrationIdPAndAriksa,
                          })
                        }
                      />
                    </Stack>
                  ),
                },
                confirm: {
                  type: 'custom',
                  component: () => (
                    <FormConfirmationField
                      header="Configure SCIM integration between Identity Provider and Snowflake"
                      subHeader="Configure identity directory provider to assign group permissions to Snowflake"
                      isChecked={state.scimIntegrationIdPAndSnowflake}
                      onClick={() =>
                        updateState({
                          scimIntegrationIdPAndSnowflake: !state.scimIntegrationIdPAndSnowflake,
                        })
                      }
                      isDisabled={!state.scimIntegrationIdPAndAriksa}
                    />
                  ),
                },
              }
            : {
                stopMessage: {
                  type: 'custom',
                  component: () => (
                    <StopSignLabel label="To proceed, indicate how Ariksa will get its permissions" />
                  ),
                },
              }),
          ...(state.identityProvider === 'okta' &&
          !state.scimIntegrationIdPAndSnowflake
            ? {}
            : {
                permissions: {
                  type: 'custom-with-form-control',
                  label: 'Setup account permissions for Ariksa',
                  component: (
                    <Box {...radioBoxStyles}>
                      <RadioGroup
                        value={state.permissionType}
                        onChange={value =>
                          updateState({ permissionType: value })
                        }
                      >
                        <HStack spacing={10}>
                          {renderRadioButton(
                            'Use Existing Role Used for Discovery ',
                            'This role already has required permissions ',
                            'existing',
                          )}
                          {renderRadioButton(
                            'Create New Role for Data Policies',
                            'Grant a purpose-built role to the Ariksa Snowflake user',
                            'create',
                          )}
                        </HStack>
                      </RadioGroup>
                    </Box>
                  ),
                },
                ...(state.permissionType === 'create'
                  ? {
                      snowflake_role: {
                        type: 'text',
                        label: 'Snowflake role name',
                        placeholder: 'Enter role name',
                        value: state.roleName,
                        onChange: roleName => updateState({ roleName }),
                        isRequired: true,
                      },
                    }
                  : {}),
                note: {
                  type: 'custom',
                  component: () => (
                    <Box w="full">
                      <WarningAlert
                        message={
                          state.permissionType === 'create'
                            ? `Ariksa will connect as the existing Snowflake user
                  and activate the role above. Please ensure the role has been
                  granted to that user and has the required permissions to
                  create access and masking policies.`
                            : `We’ll re-use the existing role in Snowflake currently
                  used to discover databases and tables. Please ensure that this role
                  has the required permissions to create access and masking policies.`
                        }
                      />
                    </Box>
                  ),
                },
              }),
        }}
        buttonOptions={{
          submit: {
            name: 'ENABLE',
            isLoading: dataAccessManagement.isLoading,
            isDisabled:
              !state.snowflakeAccount?.value ||
              (state.identityProvider === 'okta' &&
                (!state.oktaAccount?.value ||
                  !state.scimIntegrationIdPAndAriksa ||
                  !state.scimIntegrationIdPAndSnowflake)) ||
              (state.permissionType === 'create' && !state.roleName.trim()),
          },
          reset: {
            isVisible: true,
            name: 'CANCEL',
            onClick: () => {
              navigate(jitSetupPath);
            },
          },
        }}
        handleSubmit={onSubmit}
        styles={{ form: { formWidth: '800px' } }}
      />
    </Card>
  );
};
